import {
    Modifier,
    EditorState,
    ContentBlock,
    RichUtils,
    SelectionState,
    ContentState,
    BlockMap,
    Entity,
    CompositeDecorator
} from "draft-js";
import { OrderedMap } from "immutable";
import * as DraftJSUtils from "draftjs-utils";
import {
    FontFamily,
    FontProviders,
    CustomFontMetadata,
    TypekitMetadata,
    FontsDotComMetadata
} from "shared/xp/models/widgets";
import {
    FontOption,
    textEditorDefaults,
    AlignmentStates
} from "shared/ui/experience/widgets/textEnhancedWidget/TextEditorControls";
import { styles, customBlockStyleDataKeys } from "./editorStateConverters";
import * as Color from "shared/util/Color";
import {FONT_METADATA} from "./models";
import { CharacterMetadata } from "draft-js";

const emptyBlockValue = String.fromCharCode(0x200C);

/**
 * Function will merge block level meta-data.
 */
const mergeBlockData = (
    editorState: EditorState,
    // TODO: Figure out how to access immutable JS's typings
    data: any
): EditorState => {
    const newContentState = Modifier.mergeBlockData(
        editorState.getCurrentContent(),
        editorState.getSelection(),
        data
    );
    return EditorState.push(editorState, newContentState, "change-block-data");
};

/**
 * Function will get a unique list of CSS property values for given key.
 */
const getSelectionStylePropertyValues = (
    editorState: EditorState,
    cssPropertyKey: string
): Array<string> => {
    const propertyValues =
        DraftJSUtils.getSelectedBlocksList(editorState)
            .map((contentBlock: ContentBlock) => {
                    const blockData = contentBlock.getData();
                    return blockData.get(cssPropertyKey);
                })
            .reduce((accumulator: Array<string>, data: string) => {
                if (accumulator.indexOf(data) === -1) {
                    accumulator.push(data);
                }

                return accumulator;
            },      []);

    return propertyValues || [];
};

/**
 * Function will apply an inline style.
 */
const applyInlineStyle = (
    editorState: EditorState,
    styleName: string,
): EditorState => {
    const newContentState = Modifier.applyInlineStyle(
        editorState.getCurrentContent(),
        editorState.getSelection(),
        styleName
    );
    return EditorState.push(editorState, newContentState, "change-inline-style");
};

/**
 * Function will remove an inline style.
 */
const removeInlineStyle = (
    editorState: EditorState,
    styleName: string,
): EditorState => {
    const newContentState = Modifier.removeInlineStyle(
        editorState.getCurrentContent(),
        editorState.getSelection(),
        styleName
    );
    return EditorState.push(editorState, newContentState, "change-inline-style");
};

/**
 * Function will set Focus and Anchor keys to the Start key in selection
 */
const forceFocusBeginning = (
    editorState: EditorState
): EditorState => {
    const selectionState = editorState.getSelection();
    return forceFocusBeginningOfSelectionState(editorState, selectionState.getStartKey());
};

const forceFocusBeginningOfSelectionState = (
    editorState: EditorState,
    focusKey: string,
): EditorState => {
    const selectionState = editorState.getSelection();
    // TODO: Fix typing from immutable.js, SelectionState.merge does not return a SelectionState type
    const focusOffset = selectionState.getStartOffset();
    const newSelection: any = selectionState.merge({
        anchorKey: focusKey,
        focusKey: focusKey,
        anchorOffset: focusOffset || 0,
        focusOffset: focusOffset || 0
    });
    return EditorState.forceSelection(
        editorState,
        newSelection,
    );
};

/**
 * Function will set Focus and Anchor keys to the End key in selection
 */
const forceFocusEnding = (
    editorState: EditorState
): EditorState => {
    const currentContent = editorState.getCurrentContent();
    const lastBlock = currentContent.getBlockMap().last();
    const lastBlockKey = lastBlock.getKey();
    const lengthOfLastBlock = lastBlock.getLength();

    const selection = new SelectionState({
        anchorKey: lastBlockKey,
        anchorOffset: lengthOfLastBlock,
        focusKey: lastBlockKey,
        focusOffset: lengthOfLastBlock,
    });

    return EditorState.forceSelection(
        editorState,
        selection,
    );
};

/**
 * Function will set Focus and Anchor keys to the End key in the current block
 */
const forceFocusBlockEnding = (
    editorState: EditorState,
    currentContentBlock: ContentBlock
): EditorState => {
    const blockKey = currentContentBlock.getKey();
    const lengthOfBlock = currentContentBlock.getLength();

    const selection = new SelectionState({
        anchorKey: blockKey,
        anchorOffset: lengthOfBlock,
        focusKey: blockKey,
        focusOffset: lengthOfBlock,
    });

    return EditorState.forceSelection(
        editorState,
        selection,
    );
};

const forcePreviousFocus = (editorState: EditorState): EditorState => {
    const selectionState = editorState.getSelection();

    const selection = new SelectionState({
        anchorKey: selectionState.getAnchorKey(),
        anchorOffset: selectionState.getAnchorOffset(),
        focusKey: selectionState.getFocusKey(),
        focusOffset: selectionState.getFocusOffset(),
    });

    return EditorState.forceSelection(
        editorState,
        selection,
    );
};

const forceSelectAll = (
    editorState: EditorState
): EditorState => {
    const currentContent = editorState.getCurrentContent();
    const firstBlock = currentContent.getBlockMap().first();
    const lastBlock = currentContent.getBlockMap().last();
    const firstBlockKey = firstBlock.getKey();
    const lastBlockKey = lastBlock.getKey();
    const lengthOfLastBlock = lastBlock.getLength();

    const selection = new SelectionState({
        anchorKey: firstBlockKey,
        anchorOffset: 0,
        focusKey: lastBlockKey,
        focusOffset: lengthOfLastBlock,
    });

    return EditorState.forceSelection(
        editorState,
        selection,
    );
};

const forceUnselectAll = (
    editorState: EditorState
): EditorState => {
    const selectionState = editorState.getSelection();

    const newSelection = new SelectionState({
        anchorKey: selectionState.getStartKey(),
        focusKey: selectionState.getStartKey(),
        anchorOffset: 0,
        focusOffset: 0,
        hasFocus: false
    });

    return EditorState.forceSelection(
        editorState,
        newSelection,
    );
};

/**
 * Function will insert a new block copying the style and removing the block type.
 */
const splitBlockWithData = (editorState): EditorState => {
    const currentBlock: ContentBlock = DraftJSUtils.getSelectedBlock(editorState);
    if(currentBlock) {
        const currentBlockData = currentBlock.getData();

        const splitContentState = Modifier.splitBlock(
            editorState.getCurrentContent(),
            editorState.getSelection()
        );

        const splitEditorState = EditorState.push(
            editorState,
            splitContentState,
            "split-block"
        );

        const fixedBlockTypeEditorState = RichUtils.toggleBlockType(splitEditorState, "unstyled");

        return mergeBlockData(fixedBlockTypeEditorState, currentBlockData);
    } else {
        return editorState;
    }
};

const getFontMetadataEntityFromFontFamily = (fontFamilyData: FontFamily): {[key: string]: String|FontProviders} => {
    const {fontProvider, fontFamily, fontWeight, fontStyle, cssFamilyNames, url, target} = fontFamilyData;
    const entity = {
        fontFamily,
        fontStyle,
        fontWeight,
        fontProvider,
        cssFamilyNames
        };
    switch (fontFamilyData.fontProvider) {
        case FontProviders.CUSTOM:
            const {customFontId, format, path} = fontFamilyData.fontMetadata;
            Object.assign(entity, {
                customFontId,
                format,
                path
            });
            break;
        case FontProviders.TYPEKIT:
            const {kitID} = fontFamilyData.fontMetadata;
            Object.assign(entity, {
                kitID
            });
            break;
        case FontProviders.FONTSDOTCOM:
            const {projectId} = fontFamilyData.fontMetadata;
            Object.assign(entity, {
                projectId
            });
            break;
        default:
            break;
    }

    if (url !== undefined) {
        Object.assign(entity, {
            url, target
        })
    }

    return entity;
};

const getFontFamilyFromFontMetadataEntity = (entity: {[key: string]: string|FontProviders}): FontFamily => {
    const {fontProvider, fontFamily, fontWeight, fontStyle, cssFamilyNames, url, target} = entity;
    let fontProviderType;
    let fontMetadata: CustomFontMetadata|TypekitMetadata|FontsDotComMetadata;
    switch (fontProvider) {
        case FontProviders.CUSTOM:
            fontProviderType = FontProviders.CUSTOM;
            const {customFontId, format, path} = entity;
            fontMetadata = {
                customFontId,
                format,
                path
            } as CustomFontMetadata;
            break;
        case FontProviders.TYPEKIT:
            fontProviderType = FontProviders.TYPEKIT;

            const {kitID} = entity;
            fontMetadata = {
                kitID
            } as TypekitMetadata;
            break;
        case FontProviders.FONTSDOTCOM:
            fontProviderType = FontProviders.FONTSDOTCOM;

            const {projectId} = entity;
            fontMetadata = {
                projectId
            } as FontsDotComMetadata;
            break;
        case FontProviders.STANDARD:
            fontProviderType = FontProviders.STANDARD;
            break;
        case FontProviders.GOOGLE:
            fontProviderType = FontProviders.GOOGLE;
            break;
    }

    return {
        fontProvider: fontProviderType, fontFamily, fontStyle, fontWeight, fontMetadata, cssFamilyNames, url, target
    };
};

const getFontFamilyProviderMetaData = (fontOption: FontOption, fontStyle: string, fontWeight: string): FontFamily => {
    const {fontDescriptor} = fontOption;
    const fontData = fontDescriptor.get("data") || {};
    const currentFontProvider = fontDescriptor.get("provider").value;
    const cssFamilyNames = fontDescriptor.get("cssFamily");
    const {customFontId, format, path, kitID, projectId} = fontData;
    let fontProviderMetaData;
    let fontMetadata;

    switch (currentFontProvider) {
        case FontProviders.CUSTOM:
            fontMetadata = {
                customFontId,
                format,
                path
            } as CustomFontMetadata;

            fontProviderMetaData = {
                fontProvider: FontProviders.CUSTOM,
            };
            break;
        case FontProviders.TYPEKIT:
            fontMetadata = {
                kitID
            } as TypekitMetadata;
            fontProviderMetaData = {
                fontProvider: FontProviders.TYPEKIT,
                fontMetadata
            };
            break;
        case FontProviders.FONTSDOTCOM:
            fontMetadata = {
                projectId
            } as FontsDotComMetadata;
            fontProviderMetaData = {
                fontProvider: FontProviders.FONTSDOTCOM,
                fontMetadata
            };
            break;
        case FontProviders.GOOGLE:
            fontProviderMetaData = {
                fontProvider: FontProviders.GOOGLE
            };
            break;
        case FontProviders.STANDARD:
            fontProviderMetaData = {
                fontProvider: FontProviders.STANDARD
            };
            break;
        default:
            break;
    }

    Object.assign(fontProviderMetaData, {
        fontFamily: fontOption.value,
        fontWeight,
        fontStyle,
        fontMetadata,
        cssFamilyNames
    });

    return fontProviderMetaData;
};

interface SetDefaultBlockStylesParams {
    editorState: EditorState;
    textAlign?: AlignmentStates;
}

const setDefaultBlockStyles = (
    { editorState, textAlign = textEditorDefaults.alignment}: SetDefaultBlockStylesParams
): EditorState => {
    return mergeBlockData(
        editorState,
        {
            [customBlockStyleDataKeys.textAlign]: textAlign,
            [customBlockStyleDataKeys.lineHeight]: textEditorDefaults.lineHeight.toString(),
        }
    );
};

interface ApplyDefaultStylesParams {
    editorState: EditorState;
    fontWeight: string;
    fontSize: number;
    fontColor: Color.RGBA;
    currentFontFamily: string;
}

const applyDefaultStyles = (
    { editorState, fontSize, fontColor, fontWeight, currentFontFamily }: ApplyDefaultStylesParams
): EditorState => {
        editorState = setDefaultBlockStyles({ editorState });

        editorState = forceSelectAll(editorState);

        const parsedFontSize = `${fontSize}px`;

        editorState = styles.fontSize.add(editorState, parsedFontSize);
        editorState = styles.color.add(editorState, Color.formatRgbaToStyle(fontColor));
        editorState = styles.fontWeight.add(editorState, fontWeight);
        editorState = styles.fontFamily.add(editorState, currentFontFamily);

        return forceUnselectAll(editorState);
};

const setFontMetaData = (editorState: EditorState, fontFamilyData: FontFamily, decorator?: CompositeDecorator): EditorState => {
    const incomingEntityData = getFontMetadataEntityFromFontFamily(fontFamilyData)

    const contentState = editorState.getCurrentContent()
    const selection = editorState.getSelection()
    const startBlockKey = selection.getStartKey()
    const endBlockKey = selection.getEndKey()
    const startOffset = selection.getStartOffset()
    const endOffset = selection.getEndOffset()
    const blockMap = contentState.getBlockMap()
    let mergedBlockMap = blockMap

    const mergedEntities = {}

    let currentBlockKey = startBlockKey
    while (currentBlockKey) {
        const block = contentState.getBlockForKey(currentBlockKey)
        const characterList = block.getCharacterList()
        let mergedCharacterList = characterList

        let currentIndex = currentBlockKey === startBlockKey ? startOffset : 0
        const endIndex = currentBlockKey === endBlockKey ? endOffset : characterList.count()
        for (currentIndex; currentIndex < endIndex; currentIndex++) {
            const characterMetadata = characterList.get(currentIndex)
            const characterEntityKey = characterMetadata.getEntity()
            const existingEntityData = characterEntityKey ? contentState.getEntity(characterEntityKey).getData() : {}

            // merge in new entity data
            const mergedEntityData = {
                ...existingEntityData,
                ...incomingEntityData
            }

            // if existing link was removed, clear it from the merged data
            if (!!existingEntityData.url && incomingEntityData.url && incomingEntityData.url.length === 0) {
                delete mergedEntityData.url
                delete mergedEntityData.target
            }

            // check for existing matching entity key to reduce duplication
            let mergedEntityKey = mergedEntities[characterEntityKey]
            if (!mergedEntityKey) {
                mergedEntityKey = Entity.create(FONT_METADATA, 'MUTABLE', mergedEntityData)
                mergedEntities[characterEntityKey] = mergedEntityKey
            }

            // merge the new character metadata into the character list
            mergedCharacterList = mergedCharacterList.set(currentIndex, CharacterMetadata.applyEntity(characterMetadata, mergedEntityKey))
        }

        // merge the new character list into the block
        const updatedBlock = block.set('characterList', mergedCharacterList)
        // @ts-ignore
        mergedBlockMap = mergedBlockMap.merge(OrderedMap({[currentBlockKey]: updatedBlock}))

        // move to the next block (if any)
        currentBlockKey = currentBlockKey === endBlockKey ? undefined : contentState.getKeyAfter(currentBlockKey)
    }

    // merge the new blocks into the contentState
    // @ts-ignore
    const mergedEditorState = EditorState.push(editorState, contentState.merge({ blockMap: mergedBlockMap }), 'apply-entity')

    // force link decorator to be applied (fixes issue with links sometimes not being underlined correctly after being applied)
    return decorator && incomingEntityData.url ? EditorState.set(mergedEditorState, { decorator }) : mergedEditorState
};
/**
 * Function will create block map from provided text with
 * santize pasted text from `\r\n` and replace empty lines with emptyBlockValue (&zwnj;)
 */
const sanitizeTextToBlockMap = (text: string): BlockMap => {
    const pastedTextAsBlocks = ContentState.createFromText(text).getBlockMap();
    let santizedBlocks: BlockMap = OrderedMap<string, ContentBlock>();
    // 
    pastedTextAsBlocks.forEach(element => {
        const text = (element.getText() !== "") ? element.getText() : emptyBlockValue;

        const cleanBlock = new ContentBlock({
            key: element.getKey(),
            type: element.getType(),
            text: text
        })
        santizedBlocks = santizedBlocks.set(element.getKey(), cleanBlock)
    });

    return santizedBlocks;
}

/**
 * Function will insert block map to the to the selection state for the editor state.
 */
const insertBlockMapFragmet = (editorState: EditorState, blocks: BlockMap): EditorState =>{
    const newState = Modifier.replaceWithFragment(
        editorState.getCurrentContent(),
        editorState.getSelection(),
        blocks,
    );
    return EditorState.push(editorState, newState, "insert-fragment");
}

const draftJSUtilsCustom = {
    ...DraftJSUtils,
    mergeBlockData,
    getSelectionStylePropertyValues,
    applyInlineStyle,
    removeInlineStyle,
    forceFocusBeginning,
    splitBlockWithData,
    forceFocusEnding,
    forceFocusBlockEnding,
    forceSelectAll,
    forceUnselectAll,
    getFontMetadataEntityFromFontFamily,
    getFontFamilyFromFontMetadataEntity,
    getFontFamilyProviderMetaData,
    forcePreviousFocus,
    setDefaultBlockStyles,
    applyDefaultStyles,
    forceFocusBeginningOfSelectionState,
    setFontMetaData,
    sanitizeTextToBlockMap,
    insertBlockMapFragmet,
    emptyBlockValue
};

export {
    draftJSUtilsCustom as DraftJSUtils,
};



// WEBPACK FOOTER //
// ./src/shared/ui/experience/widgets/textEnhancedWidget/utility.ts